import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Add, Delete, Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProduct, createProduct, updateProduct, clearError } from '../store/slices/productSlice';
import { getCategories } from '../store/slices/categorySlice';
import { compressImage } from '../utils/imageCompressor';

const ProductForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { currentProduct, isLoading, error } = useSelector((state) => state.products || {});
  const categoriesState = useSelector((state) => state.categories);
  const { categories = [], isLoading: categoriesLoading, error: categoriesError } = categoriesState || {};
  const [successMessage, setSuccessMessage] = useState('');


  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    // category: '',
    parentCategory: '',     // 👈 NEW: Parent Category ID
    subCategory: '',
    brand: '',
    basePrice: '',
    originalPrice: '',
    discountPercentage: 0,
    sku: '',
    stock: '',
    lowStockThreshold: 5,
    isActive: true,
    isFeatured: false,
    tags: [],
    variants: [], // Structure: [{ name: 'Size', options: [{ value, price, originalPrice, discountPercentage, stock, sku, image, isActive }] }]
    images: [],
    specifications: {
      weight: '',
      dimensions: '',
      material: '',
      warranty: '',
      origin: '',
      ingredients: '',
      directions: '',
      precautions: ''
    }
  });
  const [subCategories, setSubCategories] = useState([]); // 👈 NEW STATE

  const [newTag, setNewTag] = useState('');
  const [variantGroups, setVariantGroups] = useState([]); // [{ name: 'Size', options: [...] }]
  const [newVariantGroup, setNewVariantGroup] = useState({ name: '', options: [] });
  const [newVariantOption, setNewVariantOption] = useState({
    value: '',
    price: '',
    originalPrice: '',
    discountPercentage: 0,
    stock: '',
    sku: '',
    image: '',
    isActive: true
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // 👈 NEW: Handle Parent Category Change
  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value;
    setFormData(prev => ({
      ...prev,
      parentCategory: parentId,
      subCategory: ''  // 👈 Reset sub when parent changes
    }));

    // 👈 Filter subcategories for selected parent
    if (parentId) {
      const filteredSubs = categories.filter(cat => cat.parentCategory === parentId);
      setSubCategories(filteredSubs);
    } else {
      setSubCategories([]);
    }
  };

  // 👈 NEW: Handle Sub Category Change  
  const handleSubCategoryChange = (e) => {
    setFormData(prev => ({
      ...prev,
      subCategory: e.target.value
    }));
  };

  // Get category-specific fields based on selected category
  const getCategorySpecificFields = () => {
    // Use subCategory if available, otherwise parentCategory
    const categoryId = formData.subCategory || formData.parentCategory;
    const selectedCategory = categories.find(cat => cat._id === categoryId);
    if (!selectedCategory) return null;

    const categoryName = selectedCategory.name.toLowerCase();

    // Define category-specific fields for Pakistan marketplace
    const categoryFields = {
      'fragrances': {
        specifications: {
          weight: 'Volume (ml)',
          dimensions: 'Bottle Size',
          material: 'Fragrance Type',
          warranty: 'Longevity',
          origin: 'Country of Origin'
        },
        variants: ['Volume', 'Concentration', 'Limited Edition']
      },
      'perfumes': {
        specifications: {
          weight: 'Volume (ml)',
          dimensions: 'Bottle Size',
          material: 'Fragrance Type',
          warranty: 'Longevity',
          origin: 'Country of Origin'
        },
        variants: ['Volume', 'Concentration', 'Limited Edition']
      },
      'clothing': {
        specifications: {
          weight: 'Weight (g)',
          dimensions: 'Size Chart',
          material: 'Fabric',
          warranty: 'Care Instructions',
          origin: 'Made In'
        },
        variants: ['Size', 'Color', 'Style']
      },
      'apparel': {
        specifications: {
          weight: 'Weight (g)',
          dimensions: 'Size Chart',
          material: 'Fabric',
          warranty: 'Care Instructions',
          origin: 'Made In'
        },
        variants: ['Size', 'Color', 'Style']
      },
      'electronics': {
        specifications: {
          weight: 'Weight (kg)',
          dimensions: 'Dimensions (cm)',
          material: 'Material',
          warranty: 'Warranty Period',
          origin: 'Manufacturing Country'
        },
        variants: ['Storage', 'Color', 'Model']
      },
      'home & living': {
        specifications: {
          weight: 'Weight (kg)',
          dimensions: 'Dimensions',
          material: 'Material',
          warranty: 'Durability',
          origin: 'Origin'
        },
        variants: ['Size', 'Color', 'Finish']
      },
      'beauty & personal care': {
        specifications: {
          weight: 'Weight (g/ml)',
          dimensions: 'Package Size',
          material: 'Product Type',
          warranty: 'Expiry Date',
          origin: 'Country of Origin'
        },
        variants: ['Size', 'Type', 'Variant']
      },
      'sports & fitness': {
        specifications: {
          weight: 'Weight (kg)',
          dimensions: 'Size',
          material: 'Material',
          warranty: 'Warranty',
          origin: 'Brand Origin'
        },
        variants: ['Size', 'Color', 'Model']
      },
      'jewelry': {
        specifications: {
          weight: 'Weight (g)',
          dimensions: 'Dimensions (mm)',
          material: 'Metal Type',
          warranty: 'Warranty Period',
          origin: 'Origin'
        },
        variants: ['Size', 'Metal', 'Design']
      },
      'watches': {
        specifications: {
          weight: 'Weight (g)',
          dimensions: 'Case Size (mm)',
          material: 'Case Material',
          warranty: 'Warranty Period',
          origin: 'Brand Origin'
        },
        variants: ['Size', 'Color', 'Strap Type']
      },
      'footwear': {
        specifications: {
          weight: 'Weight (g)',
          dimensions: 'Size Chart',
          material: 'Upper Material',
          warranty: 'Warranty',
          origin: 'Made In'
        },
        variants: ['Size', 'Color', 'Width']
      },
      'bags & luggage': {
        specifications: {
          weight: 'Weight (kg)',
          dimensions: 'Dimensions (cm)',
          material: 'Material',
          warranty: 'Warranty',
          origin: 'Made In'
        },
        variants: ['Size', 'Color', 'Style']
      },
      'default': {
        specifications: {
          weight: 'Weight',
          dimensions: 'Dimensions',
          material: 'Material',
          warranty: 'Warranty',
          origin: 'Origin'
        },
        variants: ['Size', 'Color', 'Variant']
      }
    };

    // Try exact match first, then partial match, then default
    if (categoryFields[categoryName]) {
      return categoryFields[categoryName];
    }

    // Check for partial matches
    for (const key in categoryFields) {
      if (categoryName.includes(key) || key.includes(categoryName)) {
        return categoryFields[key];
      }
    }

    return categoryFields['default'];
  };

  useEffect(() => {
    // Only dispatch if the action exists
    try {
      dispatch(getCategories());
    } catch (error) {
      console.error('Error dispatching getCategories:', error);
    }

    if (isEdit && id) {
      try {
        dispatch(getProduct(id));
      } catch (error) {
        console.error('Error dispatching getProduct:', error);
      }
    }
  }, [dispatch, isEdit, id]);

  useEffect(() => {
    if (isEdit && currentProduct && categories.length > 0) {
      // Handle specifications - convert Map to object if needed
      const specs = currentProduct.specifications || {};
      const specsObj = specs instanceof Map
        ? Object.fromEntries(specs)
        : (typeof specs === 'object' ? specs : {});

      // Load variants properly
      let loadedVariants = [];
      if (currentProduct.variants && currentProduct.variants.length > 0) {
        // Check if variants are in new format (with options array)
        if (currentProduct.variants[0]?.options && Array.isArray(currentProduct.variants[0].options)) {
          loadedVariants = currentProduct.variants;
        } else {
          // Convert old format to new format for backward compatibility
          const groups = {};
          currentProduct.variants.forEach(v => {
            if (!groups[v.name]) {
              groups[v.name] = { name: v.name, options: [] };
            }
            const optionValue = v.value || v.options?.[0] || 'Default';
            groups[v.name].options.push({
              value: optionValue,
              price: v.price || currentProduct.price || currentProduct.basePrice,
              originalPrice: v.originalPrice || '',
              discountPercentage: v.discountPercentage || 0,
              stock: v.stock || currentProduct.stock,
              sku: v.sku || '',
              image: v.image || '',
              isActive: v.isActive !== undefined ? v.isActive : true
            });
          });
          loadedVariants = Object.values(groups);
        }
      }

      // Determine parentCategory and subCategory from product's category
      const productCategoryId = currentProduct.category?._id || currentProduct.category || '';
      let parentCategoryId = '';
      let subCategoryId = '';

      if (productCategoryId) {
        // Find the category in the categories list
        const productCategory = categories.find(cat => cat._id === productCategoryId);

        if (productCategory) {
          // If category has a parentCategory, it's a subcategory
          if (productCategory.parentCategory) {
            parentCategoryId = productCategory.parentCategory;
            subCategoryId = productCategoryId;
            // Load subcategories for this parent
            const filteredSubs = categories.filter(cat => cat.parentCategory === parentCategoryId);
            setSubCategories(filteredSubs);
          } else {
            // It's a parent category
            parentCategoryId = productCategoryId;
            subCategoryId = '';
            // Load subcategories for this parent
            const filteredSubs = categories.filter(cat => cat.parentCategory === parentCategoryId);
            setSubCategories(filteredSubs);
          }
        }
      }

      setFormData({
        name: currentProduct.name || '',
        description: currentProduct.description || '',
        shortDescription: currentProduct.shortDescription || '',
        parentCategory: parentCategoryId,
        subCategory: subCategoryId,
        brand: currentProduct.brand || '',
        basePrice: currentProduct.price || currentProduct.basePrice || '',
        originalPrice: currentProduct.originalPrice || '',
        discountPercentage: currentProduct.discountPercentage || 0,
        sku: currentProduct.sku || '',
        stock: currentProduct.stock || '',
        lowStockThreshold: currentProduct.lowStockThreshold || 5,
        isActive: currentProduct.isActive !== undefined ? currentProduct.isActive : true,
        isFeatured: currentProduct.featured !== undefined ? currentProduct.featured : false,
        tags: currentProduct.tags || [],
        variants: loadedVariants,
        images: currentProduct.images || [],
        specifications: {
          weight: specsObj.weight || '',
          dimensions: specsObj.dimensions || '',
          material: specsObj.material || '',
          warranty: specsObj.warranty || '',
          origin: specsObj.origin || '',
          ingredients: specsObj.ingredients || '',
          directions: specsObj.directions || '',
          precautions: specsObj.precautions || ''
        }
      });

      // Set variant groups
      setVariantGroups(loadedVariants);
    }
  }, [isEdit, currentProduct, categories]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSpecificationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [name]: value
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Initialize variant groups from formData.variants
  useEffect(() => {
    if (formData.variants && formData.variants.length > 0) {
      // Check if variants are in new format (with options array)
      if (formData.variants[0]?.options && Array.isArray(formData.variants[0].options)) {
        setVariantGroups(formData.variants);
      } else {
        // Convert old format to new format
        const groups = {};
        formData.variants.forEach(v => {
          if (!groups[v.name]) {
            groups[v.name] = { name: v.name, options: [] };
          }
          groups[v.name].options.push({
            value: v.value || v.options?.[0] || '',
            price: v.price || formData.basePrice,
            originalPrice: v.originalPrice || '',
            discountPercentage: v.discountPercentage || 0,
            stock: v.stock || formData.stock,
            sku: v.sku || '',
            image: v.image || '',
            isActive: v.isActive !== undefined ? v.isActive : true
          });
        });
        setVariantGroups(Object.values(groups));
      }
    } else {
      setVariantGroups([]);
    }
  }, [formData.variants, formData.basePrice, formData.stock]);

  // Sync variantGroups to formData.variants
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      variants: variantGroups
    }));
  }, [variantGroups]);

  const handleAddVariantGroup = () => {
    if (newVariantGroup.name.trim()) {
      setVariantGroups(prev => [...prev, { ...newVariantGroup, name: newVariantGroup.name.trim() }]);
      setNewVariantGroup({ name: '', options: [] });
    }
  };

  const handleRemoveVariantGroup = (index) => {
    setVariantGroups(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddVariantOption = (groupIndex) => {
    if (newVariantOption.value.trim()) {
      const option = {
        value: newVariantOption.value.trim(),
        price: newVariantOption.price ? Number(newVariantOption.price) : formData.basePrice,
        originalPrice: newVariantOption.originalPrice ? Number(newVariantOption.originalPrice) : '',
        discountPercentage: newVariantOption.discountPercentage ? Number(newVariantOption.discountPercentage) : 0,
        stock: newVariantOption.stock ? Number(newVariantOption.stock) : formData.stock,
        sku: newVariantOption.sku || '',
        image: newVariantOption.image || '',
        isActive: newVariantOption.isActive
      };

      setVariantGroups(prev => {
        const updated = [...prev];
        updated[groupIndex] = {
          ...updated[groupIndex],
          options: [...updated[groupIndex].options, option]
        };
        return updated;
      });

      setNewVariantOption({
        value: '',
        price: '',
        originalPrice: '',
        discountPercentage: 0,
        stock: '',
        sku: '',
        image: '',
        isActive: true
      });
    }
  };

  const handleRemoveVariantOption = (groupIndex, optionIndex) => {
    setVariantGroups(prev => {
      const updated = [...prev];
      updated[groupIndex] = {
        ...updated[groupIndex],
        options: updated[groupIndex].options.filter((_, i) => i !== optionIndex)
      };
      return updated;
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);

    try {
      const uploadPromises = files.map(async (file) => {
        // Check file type before upload
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        console.log('[ProductForm] Starting upload for', {
          name: file.name,
          size: file.size,
          type: file.type,
        });

        // Compress image before upload
        let fileToUpload = file;
        try {
          console.log('[ProductForm] Compressing image...');
          fileToUpload = await compressImage(file, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          console.log('[ProductForm] Compression complete', {
            originalSize: file.size,
            compressedSize: fileToUpload.size,
          });
        } catch (compressionError) {
          console.warn('[ProductForm] Compression failed, using original:', compressionError);
          // Continue with original file if compression fails
        }

        const formData = new FormData();
        formData.append('image', fileToUpload);

        const apiBase = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
        const url = `${apiBase}/upload`;
        console.log('[ProductForm] Uploading to:', url);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        console.log('[ProductForm] /upload response status:', response.status, response.statusText);

        if (!response.ok) {
          let errorBody;
          try {
            errorBody = await response.json();
          } catch {
            errorBody = await response.text();
          }
          console.error('[ProductForm] /upload error response body:', errorBody);
          const message =
            (errorBody && errorBody.message) ||
            (typeof errorBody === 'string' ? errorBody : '') ||
            'Upload failed';
          throw new Error(message);
        }

        const result = await response.json();
        console.log('[ProductForm] /upload success payload:', result);
        return result.imageUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls],
      }));
    } catch (error) {
      console.error('[ProductForm] Image upload error:', error);
      alert(`Failed to upload images: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.parentCategory) errors.parentCategory = 'Parent category is required';
    if (!formData.name.trim()) errors.name = 'Product name is required';
    if (!formData.brand.trim()) errors.brand = 'Brand is required';
    if (!formData.basePrice || formData.basePrice <= 0) errors.basePrice = 'Valid base price is required';
    if (!formData.stock || formData.stock < 0) errors.stock = 'Valid stock quantity is required';
    if (!formData.description.trim()) errors.description = 'Description is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    setValidationErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    const { isFeatured, ...restFormData } = formData;

    // Determine which category to use: subCategory if exists, otherwise parentCategory
    const categoryId = formData.subCategory || formData.parentCategory;

    console.log(`[ProductForm] Saving product with categories:`, {
      productName: formData.name,
      parentCategory: formData.parentCategory,
      subCategory: formData.subCategory,
      finalCategoryId: categoryId
    });

    const submitData = {
      ...restFormData,
      category: categoryId, // Backend expects 'category' field
      basePrice: Number(formData.basePrice),
      price: Number(formData.basePrice), // Also set price field
      originalPrice: formData.originalPrice ? Number(formData.originalPrice) : undefined,
      discountPercentage: formData.discountPercentage ? Number(formData.discountPercentage) : 0,
      sku: formData.sku || undefined,
      stock: Number(formData.stock),
      lowStockThreshold: Number(formData.lowStockThreshold),
      featured: isFeatured,
      shortDescription: formData.shortDescription || '', // Ensure shortDescription is included
      variants: variantGroups, // Use variantGroups instead of formData.variants
      // Clean specifications - remove empty values
      specifications: Object.fromEntries(
        Object.entries(formData.specifications).filter(([_, value]) => value !== '')
      )
    };

    // Remove parentCategory and subCategory from submitData as backend doesn't expect them
    delete submitData.parentCategory;
    delete submitData.subCategory;

    try {
      if (isEdit) {
        await dispatch(updateProduct({ id, productData: submitData })).unwrap();
        setSuccessMessage('Product updated successfully!');
      } else {
        await dispatch(createProduct(submitData)).unwrap();
        setSuccessMessage('Product created successfully!');
      }

      // Navigate back after showing success message
      setTimeout(() => {
        navigate('/products');
      }, 1500);
    } catch (error) {
      // Error is handled by Redux state
      console.error('Error saving product:', error);
    }
  };

  if (isLoading || categoriesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/products')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          {isEdit ? 'Edit Product' : 'Add New Product'}
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {categoriesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Categories Error: {categoriesError}
        </Alert>
      )}

      {Object.keys(validationErrors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Validation errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {Object.values(validationErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                required
                error={!!validationErrors.brand}
                helperText={validationErrors.brand}
              />
            </Grid>
            {/* 👈 NEW: 2-FIELD CATEGORY SYSTEM */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!validationErrors.parentCategory}>
                <InputLabel>Parent Category *</InputLabel>
                <Select
                  name="parentCategory"
                  value={formData.parentCategory}
                  onChange={handleParentCategoryChange}
                  label="Parent Category *"
                >
                  <MenuItem value="">
                    <em>Select Parent Category</em>
                  </MenuItem>
                  {categories
                    .filter(cat => !cat.parentCategory) // 👈 ONLY PARENTS
                    .map((parent) => (
                      <MenuItem key={parent._id} value={parent._id}>
                        {parent.name}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!validationErrors.subCategory}>
                <InputLabel>Sub Category</InputLabel>
                <Select
                  name="subCategory"
                  value={formData.subCategory}
                  onChange={handleSubCategoryChange}
                  label="Sub Category"
                  disabled={!formData.parentCategory || subCategories.length === 0}
                >
                  <MenuItem value="">
                    <em>{formData.parentCategory ? 'Select Sub Category' : 'First select parent'}</em>
                  </MenuItem>
                  {subCategories.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {sub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* 👈 DISPLAY SELECTION SUMMARY */}
            {formData.parentCategory && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f0f8ff' }}>
                  <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                    {categories.find(cat => cat._id === formData.parentCategory)?.name || 'Unknown'}
                    {formData.subCategory && ` > ${categories.find(cat => cat._id === formData.subCategory)?.name}`}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!validationErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories && categories.length > 0 ? (
                    categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {categoriesLoading ? 'Loading categories...' : 'No categories available. Please add categories first.'}
                    </MenuItem>
                  )}
                </Select>
                {validationErrors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {validationErrors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Base Price"
                name="basePrice"
                type="number"
                value={formData.basePrice}
                onChange={handleChange}
                required
                error={!!validationErrors.basePrice}
                helperText={validationErrors.basePrice}
                InputProps={{
                  startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Original Price (Before Discount)"
                name="originalPrice"
                type="number"
                value={formData.originalPrice}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                }}
                helperText="Leave empty if no discount"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Discount Percentage"
                name="discountPercentage"
                type="number"
                value={formData.discountPercentage}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                helperText="Auto-calculated if original price is set"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SKU (Product Code)"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="e.g., PROD-001"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                name="stock"
                type="number"
                value={formData.stock}
                onChange={handleChange}
                required
                error={!!validationErrors.stock}
                helperText={validationErrors.stock}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Low Stock Threshold"
                name="lowStockThreshold"
                type="number"
                value={formData.lowStockThreshold}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short Description"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                required
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Grid>

            {/* Product Images */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Product Images
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={2}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  multiple
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Add />}
                    sx={{ mb: 2 }}
                    disabled={uploadingImages}
                  >
                    {uploadingImages ? 'Uploading...' : 'Upload Images'}
                  </Button>
                </label>
                {uploadingImages && (
                  <Box sx={{ mt: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      Uploading images...
                    </Typography>
                  </Box>
                )}
              </Box>

              {formData.images.length > 0 && (
                <Grid container spacing={2} key={`images-${formData.images.length}`}>
                  {formData.images.map((image, index) => {
                    const imageUrl = image.startsWith('http') ? image :
                      `${process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com'}${image}`;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={`image-${index}-${image}`}>
                        <Card>
                          <CardMedia
                            component="img"
                            height="140"
                            image={imageUrl}
                            alt={`Product image ${index + 1}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                            onLoad={() => {
                              // Image loaded successfully
                            }}
                            sx={{
                              objectFit: 'cover',
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #ddd'
                            }}
                          />
                          <CardContent>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleRemoveImage(index)}
                              startIcon={<Delete />}
                            >
                              Remove
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {formData.images.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  No images uploaded yet. Click "Upload Images" to add product photos.
                </Typography>
              )}
            </Grid>

            {/* Category-Specific Fields - Display below images */}
            {(formData.parentCategory || formData.subCategory) && (() => {
              const categoryFields = getCategorySpecificFields();
              if (!categoryFields) return null;

              const categoryId = formData.subCategory || formData.parentCategory;
              const selectedCategory = categories.find(cat => cat._id === categoryId);

              return (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {selectedCategory?.name} - Product Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={categoryFields.specifications.weight}
                        name="weight"
                        value={formData.specifications.weight}
                        onChange={handleSpecificationChange}
                        placeholder={`Enter ${categoryFields.specifications.weight.toLowerCase()}`}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={categoryFields.specifications.dimensions}
                        name="dimensions"
                        value={formData.specifications.dimensions}
                        onChange={handleSpecificationChange}
                        placeholder={`Enter ${categoryFields.specifications.dimensions.toLowerCase()}`}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={categoryFields.specifications.material}
                        name="material"
                        value={formData.specifications.material}
                        onChange={handleSpecificationChange}
                        placeholder={`Enter ${categoryFields.specifications.material.toLowerCase()}`}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={categoryFields.specifications.warranty}
                        name="warranty"
                        value={formData.specifications.warranty}
                        onChange={handleSpecificationChange}
                        placeholder={`Enter ${categoryFields.specifications.warranty.toLowerCase()}`}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={categoryFields.specifications.origin}
                        name="origin"
                        value={formData.specifications.origin}
                        onChange={handleSpecificationChange}
                        placeholder={`Enter ${categoryFields.specifications.origin.toLowerCase()}`}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })()}

            {/* Tags */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TextField
                  label="Add Tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  size="small"
                />
                <Button onClick={handleAddTag} startIcon={<Add />}>
                  Add
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    sx={{
                      borderColor: '#ff2852',
                      color: '#ff2852',
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>

            {/* Variants - SHEIN Style */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Product Variants (Size, Color, etc.)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Add New Variant Group (e.g., Size, Color) */}
              <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                    Add Variant Group (e.g., Size, Color, Storage)
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Variant Type</InputLabel>
                        <Select
                          value={newVariantGroup.name}
                          onChange={(e) => setNewVariantGroup(prev => ({ ...prev, name: e.target.value }))}
                          label="Variant Type"
                        >
                          {(() => {
                            const categoryFields = getCategorySpecificFields();
                            const variantTypes = categoryFields?.variants || ['Size', 'Color', 'Variant'];
                            return variantTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ));
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Button
                        onClick={handleAddVariantGroup}
                        variant="contained"
                        startIcon={<Add />}
                        disabled={!newVariantGroup.name}
                        sx={{ backgroundColor: '#ff2852', color: '#000' }}
                      >
                        Add Variant Group
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Display Variant Groups */}
              {variantGroups.map((group, groupIndex) => (
                <Card key={groupIndex} sx={{ mb: 3, border: '2px solid #ff2852' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff2852' }}>
                        {group.name}
                      </Typography>
                      <IconButton onClick={() => handleRemoveVariantGroup(groupIndex)} color="error" size="small">
                        <Delete />
                      </IconButton>
                    </Box>

                    {/* Add Option to this Group */}
                    <Card sx={{ mb: 2, backgroundColor: '#fafafa' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                          Add {group.name} Option
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="Value"
                              size="small"
                              value={newVariantOption.value}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, value: e.target.value }))}
                              placeholder="e.g., Small, Red, 128GB"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="Price"
                              type="number"
                              size="small"
                              value={newVariantOption.price}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, price: e.target.value }))}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="Original Price"
                              type="number"
                              size="small"
                              value={newVariantOption.originalPrice}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, originalPrice: e.target.value }))}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">PKR</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="Discount %"
                              type="number"
                              size="small"
                              value={newVariantOption.discountPercentage}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, discountPercentage: e.target.value }))}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="Stock"
                              type="number"
                              size="small"
                              value={newVariantOption.stock}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, stock: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              fullWidth
                              label="SKU"
                              size="small"
                              value={newVariantOption.sku}
                              onChange={(e) => setNewVariantOption(prev => ({ ...prev, sku: e.target.value }))}
                              placeholder="e.g., PROD-S-RED"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              onClick={() => handleAddVariantOption(groupIndex)}
                              variant="outlined"
                              startIcon={<Add />}
                              disabled={!newVariantOption.value}
                              size="small"
                            >
                              Add {group.name} Option
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    {/* Display Options in this Group */}
                    {group.options && group.options.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                          {group.name} Options:
                        </Typography>
                        <Grid container spacing={1}>
                          {group.options.map((option, optionIndex) => (
                            <Grid item xs={12} sm={6} md={4} key={optionIndex}>
                              <Card sx={{ border: '1px solid #ddd' }}>
                                <CardContent sx={{ p: 1.5 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box flex={1}>
                                      <Typography variant="body2" fontWeight={600}>
                                        {option.value}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Price: PKR {option.price?.toLocaleString() || option.price}
                                        {option.originalPrice && ` (Was: PKR ${option.originalPrice.toLocaleString()})`}
                                        {option.discountPercentage > 0 && ` | ${option.discountPercentage}% OFF`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Stock: {option.stock} | SKU: {option.sku || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      onClick={() => handleRemoveVariantOption(groupIndex, optionIndex)}
                                      color="error"
                                      size="small"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}

              {variantGroups.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No variants added yet. Add variant groups (Size, Color, etc.) and their options above.
                </Alert>
              )}
            </Grid>


            {/* Additional Product Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ingredients"
                name="ingredients"
                value={formData.specifications.ingredients}
                onChange={handleSpecificationChange}
                multiline
                rows={2}
                placeholder="e.g., Alcohol, Fragrance, Aqua (Water)"
                helperText="List product ingredients"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Directions"
                name="directions"
                value={formData.specifications.directions}
                onChange={handleSpecificationChange}
                multiline
                rows={2}
                placeholder="e.g., Apply to pulse points, Store in a cool, dry place"
                helperText="How to use the product"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Precautions"
                name="precautions"
                value={formData.specifications.precautions}
                onChange={handleSpecificationChange}
                multiline
                rows={2}
                placeholder="e.g., For external use only. Keep away from heat and flame."
                helperText="Safety warnings and precautions"
              />
            </Grid>

            {/* Status Switches */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange}
                    name="isActive"
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    name="isFeatured"
                  />
                }
                label="Featured"
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/products')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={isLoading}
                >
                  {isEdit ? 'Update Product' : 'Create Product'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ProductForm;
